import React, { useState, useEffect, ChangeEvent, useCallback, useRef } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { Alert, Col, InputGroupAddon, Row } from 'reactstrap';
import Swal from 'sweetalert2';
import debounce from 'lodash.debounce';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import 'react-datepicker/dist/react-datepicker.css';
import removeKeysWithNullValues from '../../../utils';
import apiResponseHandler from '../../../utils/apiResponseHandler';
import { environment } from '../../../environment/environment';
import { sanitizeMobileNumber } from '../User/UserDetail';
import CustomIntlTelInput, {
  isMobileNumberValid,
} from '../../../components/Users/CustomIntlTelInput';
import {
  EmployeeData,
  Employer,
  Subscription,
  TransactionFee,
  PhoneNumber,
  FormErrors,
  REASON_CATEGORIES,
  ReasonCategory,
} from './types';

interface EmployeeDetailsFormProps {
  employeeData: EmployeeData;
  getEmployeeData: () => void;
}

const EmployeeDetailsForm: React.FC<EmployeeDetailsFormProps> = ({
  employeeData,
  getEmployeeData,
}) => {
  const fieldWidth = 7;
  const labelWidth = 5;
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const [saveData, setSaveData] = useState<EmployeeData>(employeeData);
  const [feeModel, setFeeModel] = useState<'subscription' | 'transaction_fee'>(
    employeeData.subscription ? 'subscription' : 'transaction_fee'
  );
  const [employers, setEmployers] = useState<Employer[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [transactionFees, setTransactionFees] = useState<TransactionFee[]>([]);
  const [reasonCategory, setReasonCategory] = useState<ReasonCategory | null>(
    null
  );
  const [reasonText, setReasonText] = useState<string | null>(null);
  const [formError, setFormError] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({
    mobileNumberErrors: [],
    fallbackNumberErrors: [],
  });
  const [mobileNumberExists, setMobileNumberExists] = useState(false);
  const [fallbackNumberExists, setFallbackNumberExists] = useState(false);
  const [mobileNumber, setMobileNumber] = useState<PhoneNumber>({
    value: employeeData.mobile_number,
    country_code: '27',
    valid: !!employeeData.mobile_number,
  });
  const [fallbackNumber, setFallbackNumber] = useState<PhoneNumber>({
    value: employeeData.fallback_number || '',
    country_code: '27',
    valid: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [convertToPermanent, setConvertToPermanent] = useState(false);

  useEffect(() => {
    getEmployers();
    getSubscriptions();
    getTransactionFees();
    getEmployerPayrolls();
    initializeEmployerFields();
  }, []);

  const getEmployers = () => {
    axios
      .get(`${environment.baseUrl}/employers?status=active`)
      .then((res) => {
        setEmployers(res.data);
      });
  };

  const getEmployerPayrolls = (employerId?: number) => {
    const id = employerId || employeeData.employer?.id;
    if (id) {
      const url = `${environment.baseUrl}/employers/${id}/`;
      axios.get(url).then((res) => {
        setSaveData((prevData) => ({
          ...prevData,
          employer: res.data,
        }));
      });
    }
  };

  const getSubscriptions = (employerId?: number) => {
    const id = employerId || employeeData.employer?.id;
    if (id) {
      const url = `${environment.baseUrl}/subscriptions/`;
      axios
        .get(url, { params: { employer_id: id } })
        .then((res) => {
          setSubscriptions(res.data);
        });
    }
  };

  const getTransactionFees = (employerId?: number) => {
    const id = employerId || employeeData.employer?.id;
    if (id) {
      const url = `${environment.baseUrl}/transactionfees/`;
      axios
        .get(url, { params: { employer_id: id } })
        .then((res) => {
          setTransactionFees(res.data);
        });
    }
  };

  const getUsersDebounced = useCallback(
    debounce((params: any) => {
      setIsLoading(true);

      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Operation canceled due to new request.');
      }

      cancelTokenRef.current = axios.CancelToken.source();

      axios
        .get(`${environment.baseUrl}/employees/`, {
          params: { ...params, pagination: false },
          cancelToken: cancelTokenRef.current.token,
        })
        .then((res) => {
          const currentMobileNumber = sanitizeMobileNumber(mobileNumber.value);
          const currentFallBackNumber = sanitizeMobileNumber(
            fallbackNumber.value
          );

          const mobileExists =
            res.data.find(
              (employee: any) =>
                (employee.mobile_number === currentMobileNumber &&
                  employee.id !== saveData.id) ||
                employee.fallback_number === currentMobileNumber
            ) !== undefined;

          const fallbackExists =
            res.data.find(
              (employee: any) =>
                (employee.fallback_number === currentFallBackNumber &&
                  employee.id !== saveData.id) ||
                employee.mobile_number === currentFallBackNumber
            ) !== undefined;

          setMobileNumberExists(mobileExists);
          setFallbackNumberExists(fallbackExists);
          setIsLoading(false);
          validateInputs();
        })
        .catch(() => {
          setIsLoading(false);
        });
    }, 1000),
    [mobileNumber, fallbackNumber, saveData.id]
  );

  const handleEmployerChange = (event: ChangeEvent<HTMLSelectElement>) => {
    event.preventDefault();
    const selectedEmployer = employers.find(e => e.id === parseInt(event.target.value));

    if (selectedEmployer) {
      setSaveData((prevData) => ({
        ...prevData,
        employer: selectedEmployer,
        employee_branch_id: undefined,
        employee_branch: undefined,
        employee_pay_cycle_id: undefined,
        subscription: null,
        transaction_fee: null,
      }));
      getEmployerPayrolls(selectedEmployer.id);
      getSubscriptions(selectedEmployer.id);
      getTransactionFees(selectedEmployer.id);
    }
  };

  const validateInputs = (): boolean => {
    const newErrors: FormErrors = {
      mobileNumberErrors: [],
      fallbackNumberErrors: [],
    };

    const sanitizedMobile = sanitizeMobileNumber(mobileNumber.value);
    const isMobileEmpty = !sanitizedMobile || sanitizedMobile === mobileNumber.country_code;
    if (!isMobileEmpty && !mobileNumber.valid) {
      (newErrors.mobileNumberErrors as string[]).push(
        'User mobile number is incorrect.'
      );
    }

    const sanitizedFallback = sanitizeMobileNumber(fallbackNumber.value);
    const isFallbackEmpty = !sanitizedFallback || sanitizedFallback === fallbackNumber.country_code;
    if (!isFallbackEmpty && !fallbackNumber.valid) {
      (newErrors.fallbackNumberErrors as string[]).push(
        'User Fallback number is incorrect.'
      );
    }

    if (fallbackNumber.valid && fallbackNumberExists) {
      (newErrors.fallbackNumberErrors as string[]).push(
        'User already exists for the provided Fallback number.'
      );
    }

    setErrors(newErrors);
    const hasErrors =
      Object.values(newErrors).flat().length > 0;
    setFormError(hasErrors);

    return !hasErrors;
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    event.preventDefault();
    const { name, value } = event.target;
    setSaveData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handlePhoneNumberBlur = () => {
    const isMobileNumberUpdated =
      employeeData.mobile_number !== sanitizeMobileNumber(mobileNumber.value);

    if (
      isMobileNumberUpdated &&
      mobileNumber &&
      mobileNumber.valid &&
      sanitizeMobileNumber(mobileNumber.value).trim().length !== 0
    ) {
      getUsersDebounced({
        search: sanitizeMobileNumber(mobileNumber.value),
      });
    }

    const isFallbackNumberUpdated =
      (employeeData.fallback_number || '') !==
      sanitizeMobileNumber(fallbackNumber.value);

    if (
      isFallbackNumberUpdated &&
      fallbackNumber &&
      fallbackNumber.valid &&
      sanitizeMobileNumber(fallbackNumber.value).trim().length !== 0
    ) {
      getUsersDebounced({
        search: sanitizeMobileNumber(fallbackNumber.value),
      });
    }
    validateInputs();
  };

  const handleReasonChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    event.preventDefault();
    const { name, value } = event.target;
    if (name === 'reason_category') {
      setReasonCategory(value as ReasonCategory);
    } else if (name === 'reason_text') {
      setReasonText(value);
    }
  };

  const validateRequiredField = (
    fieldValue: any,
    errorMessage: string
  ): boolean => {
    if (!fieldValue || fieldValue === '') {
      Swal.fire({
        icon: 'warning',
        title: 'Employee profile was not saved',
        text: errorMessage,
        position: 'top',
        showConfirmButton: false,
        timer: 10000,
        timerProgressBar: true,
      });
      return false;
    }
    return true;
  };

  const assignPayloadField = (
    payload: any,
    sourceField: string,
    targetField: string
  ) => {
    if (payload[sourceField]) {
      payload[targetField] = payload[sourceField];
    } else {
      delete payload[targetField];
    }
  };

  const initializeEmployerFields = () => {
    setSaveData((prevData) => {
      const updates: any = {};

      if (
        employeeData.employee_pay_cycle?.id &&
        !prevData.employee_pay_cycle_id
      ) {
        updates.employee_pay_cycle_id = employeeData.employee_pay_cycle.id;
      }

      if (employeeData.employee_branch?.id && !prevData.employee_branch_id) {
        updates.employee_branch_id = employeeData.employee_branch.id;
      }

      if (Object.keys(updates).length > 0) {
        return {
          ...prevData,
          ...updates,
        };
      }

      return prevData;
    });
  };

  // Flip a temporary profile to permanent. Reuses the form's own pay cycle and
  // fee fields (already persisted by the preceding PATCH), so there are no
  // duplicate convert-only inputs.
  const convertTemporaryToPermanent = () => {
    const payload: Record<string, string | number> = {
      employee_pay_cycle: saveData.employee_pay_cycle_id as string | number,
    };
    if (feeModel === 'subscription') {
      payload.subscription = saveData.subscription as string | number;
    } else {
      payload.transaction_fee = saveData.transaction_fee as string | number;
    }

    return axios.post(
      `${environment.baseUrl}/employees/${saveData.id}/convert-to-permanent/`,
      payload
    );
  };

  const handleSaveData = () => {
    if (!validateInputs()) {
      return;
    }

    const converting = Boolean(saveData.is_temporary) && convertToPermanent;
    // Temporary profiles legitimately have no pay cycle / fee, so those
    // requirements are muted while the profile stays temporary. They are
    // enforced for permanent employees and whenever a temporary profile is
    // being converted to permanent.
    const enforcePermanentFields = !saveData.is_temporary || converting;

    if (
      enforcePermanentFields &&
      !validateRequiredField(
        saveData.employee_pay_cycle_id,
        'Employee changes not saved! Please select a pay cycle for this employee.'
      )
    ) {
      return;
    }

    if (converting) {
      const selectedFee =
        feeModel === 'subscription'
          ? saveData.subscription
          : saveData.transaction_fee;
      if (
        !validateRequiredField(
          selectedFee,
          'Conversion not started! Please select a subscription or transaction fee for this employee.'
        )
      ) {
        return;
      }
    }

    const payload: any = removeKeysWithNullValues(saveData);

    if (saveData.start_of_employment_date !== undefined) {
      payload.start_of_employment_date = saveData.start_of_employment_date;
    }

    payload.reason_category = reasonCategory;
    payload.reason_text = reasonText;
    const mobileDigits = sanitizeMobileNumber(mobileNumber.value);
    payload.mobile_number = (!mobileDigits || mobileDigits === mobileNumber.country_code) ? '' : mobileDigits;
    const fallbackDigits = sanitizeMobileNumber(fallbackNumber.value);
    payload.fallback_number = (!fallbackDigits || fallbackDigits === fallbackNumber.country_code) ? null : fallbackDigits;

    if (payload.risky === 'false') {
      payload.risky_reasons = null;
    }

    if (payload.employer) {
      payload.employer = payload.employer?.id;

      const hasBranches =
        Array.isArray(saveData.employer?.branches) &&
        saveData.employer.branches.length > 0;
      if (
        hasBranches &&
        enforcePermanentFields &&
        !validateRequiredField(
          saveData.employee_branch_id,
          'Employee changes not saved! Please set a new employer branch for this employee.'
        )
      ) {
        return;
      }
    }

    assignPayloadField(payload, 'employee_branch_id', 'employee_branch');
    assignPayloadField(payload, 'employee_pay_cycle_id', 'employee_pay_cycle');

    const url = `${environment.baseUrl}/employees/${saveData.id}/`;

    axios
      .patch(url, payload)
      .then(() => {
        // When converting, the PATCH has persisted the permanent fields; now
        // flip is_temporary via the dedicated endpoint (one-way, audited).
        if (converting) {
          return convertTemporaryToPermanent().then(() => {
            Swal.fire({
              title: 'Employee converted to permanent',
              icon: 'success',
              toast: true,
              position: 'top',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
            });
            setConvertToPermanent(false);
            getEmployeeData();
          });
        }
        Swal.fire({
          title: 'Employee profile saved',
          icon: 'success',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        getEmployeeData();
        return undefined;
      })
      .catch((error) => {
        if (error.response?.status >= 400 && error.response?.status < 500) {
          setErrors({
            mobileNumberErrors: error.response.data,
            fallbackNumberErrors: error.response.data,
          });
          setFormError(true);
        } else {
          apiResponseHandler.handleApiError('Error', error);
        }
      });
  };

  const renderEmployerDropdown = (
    fieldName: string,
    fieldValue: number | undefined,
    options: Array<{ id: number; name: string }> | undefined,
    placeholder: string
  ) => (
    <select
      className="form-control"
      name={fieldName}
      value={fieldValue || ''}
      onChange={handleInputChange}
      required
    >
      <option value="">{placeholder}</option>
      {saveData.employer &&
        options &&
        options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
    </select>
  );

  const renderEmployerDropdownField = (
    fieldName: string,
    fieldValue: number | undefined,
    options: Array<{ id: number; name: string }> | undefined,
    labelText: string,
    placeholder: string
  ) => (
    <Row className="mb-3">
      <Col lg={labelWidth}>
        <label style={{ fontWeight: '800' }} htmlFor={fieldName}>
          {labelText}
        </label>
      </Col>
      <Col lg={fieldWidth}>
        {renderEmployerDropdown(fieldName, fieldValue, options, placeholder)}
      </Col>
    </Row>
  );

  const renderStandardDropdown = (props: {
    fieldName: string;
    value: string | number;
    options: Array<{ value: string | number; label: string }>;
    labelText: string;
    onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
    placeholder?: string | null;
  }) => {
    const {
      fieldName,
      value,
      options,
      labelText,
      onChange = handleInputChange,
      placeholder = null,
    } = props;

    return (
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor={fieldName}>
            {labelText}
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <select
            className="form-control"
            name={fieldName}
            value={value || ''}
            onChange={onChange}
            required
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Col>
      </Row>
    );
  };

  return (
    <div style={{ maxWidth: '670px', margin: 'auto' }}>
      {formError && (
        <Alert color="danger" className="mb-4">
          {(() => {
            if (typeof errors.mobileNumberErrors === 'string') {
              return <div>{errors.mobileNumberErrors}</div>;
            }
            if (Array.isArray(errors.mobileNumberErrors)) {
              return errors.mobileNumberErrors.map((error, index) => (
                <div key={`error-${index}-${error}`}>{error}</div>
              ));
            }
            if (typeof errors.mobileNumberErrors === 'object') {
              return Object.entries(errors.mobileNumberErrors).map(
                ([field, messages]) => {
                  if (Array.isArray(messages)) {
                    return messages.map((message, i) => (
                      <div key={`${field}-error-${i}`}>{message}</div>
                    ));
                  }
                  if (typeof messages === 'string') {
                    return <div key={`${field}-error`}>{messages}</div>;
                  }
                  return null;
                }
              );
            }
            return null;
          })()}
        </Alert>
      )}
      {renderStandardDropdown({
        fieldName: 'smartwage_status',
        value: saveData.smartwage_status,
        options: [
          { value: 'active', label: 'Active' },
          { value: 'suspended', label: 'Suspended' },
          { value: 'deleted', label: 'Deleted' },
        ],
        labelText: 'Smartwage Status',
      })}
      {saveData.is_temporary && (
        <Row className="mb-3">
          <Col lg={labelWidth}>
            <label style={{ fontWeight: '800' }} htmlFor="convert_to_permanent">
              Convert to Permanent
            </label>
          </Col>
          <Col lg={fieldWidth}>
            <div className="custom-control custom-checkbox">
              <input
                type="checkbox"
                className="custom-control-input"
                id="convert_to_permanent"
                checked={convertToPermanent}
                onChange={(e) => setConvertToPermanent(e.target.checked)}
              />
              <label
                className="custom-control-label"
                htmlFor="convert_to_permanent"
              >
                Convert this temporary profile to permanent on save
              </label>
              <small className="form-text text-muted">
                Requires a pay cycle and a subscription or transaction fee. This
                is a one-way change.
              </small>
            </div>
          </Col>
        </Row>
      )}
      <hr className="mb-3" />
      <Row className="mb-3">
        <Col md={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="employer">
            Employer
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <select
            className="form-control"
            name="employer"
            value={saveData.employer?.id || ''}
            onChange={handleEmployerChange}
          >
            <option value="" disabled>Select Employer</option>
            {employers.map((employer) => (
              <option key={employer.id} value={employer.id}>
                {employer.company_name}
              </option>
            ))}
          </select>
        </Col>
      </Row>
      {renderEmployerDropdownField(
        'employee_branch_id',
        saveData.employee_branch_id,
        saveData.employer?.branches,
        'Branch',
        'Select branch'
      )}
      <Row>
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="employee_no">
            Employee no.
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <input
            type="text"
            name="employee_no"
            value={saveData.employee_no}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Employee Number"
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="start_of_employment_date">
            Start of employment
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <DatePicker
            id="start_of_employment_date"
            selected={
              saveData.start_of_employment_date
                ? new Date(`${saveData.start_of_employment_date}T00:00:00`)
                : null
            }
            onChange={(date: Date | null) => {
              setSaveData((prevData) => ({
                ...prevData,
                start_of_employment_date: date
                  ? dayjs(date).format('YYYY-MM-DD')
                  : null,
              }));
            }}
            dateFormat="yyyy-MM-dd"
            className="form-control"
            placeholderText="Select date"
            isClearable
          />
        </Col>
      </Row>
      <hr />
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="employee_pay_cycle_id">
            Salary
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <div className="input-group">
            <InputGroupAddon addonType="prepend">R</InputGroupAddon>
            <input
              type="number"
              name="salary"
              value={saveData.salary}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Salary"
            />
          </div>
        </Col>
      </Row>
      {renderEmployerDropdownField(
        'employee_pay_cycle_id',
        saveData.employee_pay_cycle_id,
        saveData.employer?.pay_cycles,
        'Employer pay cycle',
        'Select pay cycle'
      )}
      <hr />
      {renderStandardDropdown({
        fieldName: 'fee_model',
        value: feeModel,
        options: [
          { value: 'subscription', label: 'Subscription' },
          { value: 'transaction_fee', label: 'Transaction Fee' },
        ],
        labelText: 'Fee model',
        onChange: (e) => {
          setFeeModel(e.target.value as 'subscription' | 'transaction_fee');
          setSaveData((prevData) => ({
            ...prevData,
            subscription: null,
            transaction_fee: null,
          }));
        },
      })}
      {feeModel === 'subscription' &&
        renderStandardDropdown({
          fieldName: 'subscription',
          value: saveData.subscription || '',
          options: [
            { value: '', label: 'None' },
            ...subscriptions.map((sub) => ({ value: sub.id, label: sub.name })),
          ],
          labelText: 'Subscription',
        })}
      {feeModel === 'transaction_fee' &&
        renderStandardDropdown({
          fieldName: 'transaction_fee',
          value: saveData.transaction_fee || '',
          options: [
            { value: '', label: 'None' },
            ...transactionFees.map((fee) => ({
              value: fee.id,
              label: `(${fee.id}) ${fee.fee_type} - R${fee.fee_amount} + ${fee.fee_percentage}%`,
            })),
          ],
          labelText: 'Transaction fee',
        })}
      <hr />
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="bank_name">
            Bank name
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <input
            type="text"
            name="bank_name"
            value={saveData.bank_name}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Bank name"
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="bank_branch_code">
            Branch code
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <input
            type="text"
            name="bank_branch_code"
            value={saveData.bank_branch_code}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Bank Branch Code"
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="bank_account_number">
            Account number
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <input
            type="text"
            name="bank_account_number"
            value={saveData.bank_account_number}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Bank Account Number"
          />
        </Col>
      </Row>
      <hr />
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="fee_value">
            Risk metric
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <input
            type="number"
            name="risk_metric"
            value={saveData.risk_metric}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Risk metric"
          />
        </Col>
      </Row>
      <hr />
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="first_name">
            First name
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <input
            type="text"
            name="first_name"
            value={saveData.first_name}
            onChange={handleInputChange}
            className="form-control"
            placeholder="First Name"
            required
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="last_name">
            Last name
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <input
            type="text"
            name="last_name"
            value={saveData.last_name}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Last Name"
            required
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="id_number">
            ID Number
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <input
            type="text"
            name="id_number"
            value={saveData.id_number}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Id Number"
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="mobile_number">
            Mobile number
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <CustomIntlTelInput
            customValidation={isMobileNumberValid}
            onPhoneNumberBlur={handlePhoneNumberBlur}
            containerClassName="intl-tel-input"
            style={{ width: '100%' }}
            inputClassName="form-control"
            value={`${mobileNumber?.value ? '+' : ''}${sanitizeMobileNumber(
              mobileNumber.value
            )}`}
            formatOnInit
            onPhoneNumberChange={(
              valid: boolean,
              phone: string,
              selectedCountryData: any,
              fullNumber: string
            ) => {
              setMobileNumber({
                country_code: selectedCountryData.dialCode || '27',
                value: fullNumber,
                valid,
              });
            }}
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="fallback_number">
            Fallback Mobile number
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <CustomIntlTelInput
            customValidation={isMobileNumberValid}
            onPhoneNumberBlur={handlePhoneNumberBlur}
            containerClassName="intl-tel-input"
            style={{ width: '100%' }}
            inputClassName="form-control"
            value={`${
              fallbackNumber?.value ? '+' : ''
            }${sanitizeMobileNumber(fallbackNumber.value)}`}
            formatOnInit
            isRequired={false}
            onPhoneNumberChange={(
              valid: boolean,
              phone: string,
              selectedCountryData: any,
              fullNumber: string
            ) => {
              setFallbackNumber({
                country_code: selectedCountryData.dialCode || '27',
                value: fullNumber,
                valid,
              });
            }}
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="email">
            Email
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <input
            type="email"
            name="email"
            value={saveData.email}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Email"
            required
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="reason_category">
            Category
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <select
            className="form-control"
            name="reason_category"
            value={reasonCategory || ''}
            onChange={handleReasonChange}
            required
          >
            <option value="" disabled>
              Select Category
            </option>
            {REASON_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category.toUpperCase().replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="reason_text">
            Reason
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <input
            type="text"
            value={reasonText || ''}
            name="reason_text"
            className="form-control"
            placeholder="Reason"
            onChange={handleReasonChange}
          />
        </Col>
      </Row>
      {renderStandardDropdown({
        fieldName: 'term_cond',
        value: String(saveData.term_cond),
        options: [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
        labelText: 'Accept Ts and Cs',
        onChange: handleInputChange,
        placeholder: 'Select',
      })}
      <hr />
      {((errors.mobileNumberErrors as string[]).length > 0 ||
        (errors.fallbackNumberErrors as string[]).length > 0) && (
        <Alert color="danger">
          {Object.values(errors)
            .flat()
            .map((error, index) => (
              <li key={typeof error === 'string' ? error : `error-${index}`}>
                {typeof error === 'object'
                  ? 'An unexpected error occurred.'
                  : error}
              </li>
            ))}
        </Alert>
      )}
      <Row>
        <Col>
          <button
            disabled={!reasonCategory || isLoading}
            onClick={handleSaveData}
            className="btn btn-primary btn-block"
          >
            Save changes
          </button>
        </Col>
      </Row>
    </div>
  );
};

export default EmployeeDetailsForm;
